import { CareNet, SystemName } from "constants/captions";

export const wait = async (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValid = (value: any) => value !== null && value !== undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isObject = (value: any) => isValid(value) && typeof value === 'object';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isArray = (value: any) => isValid(value) && Array.isArray(value);

declare global {
  interface Number {
    round: (n: number) => number | null,
    numberFormat: (nullText: string) => string
  }
}
export const round = (v: number, n = 0) => {
  if (n <= 0) return Math.round(v);
  const base = Math.pow(10, n);
  return Math.round(v * base) / base;
};
/*
if (Number.prototype.round === undefined) Number.prototype.round = function (this: number, n: number = 0): number | null {
  if (this === null || this === undefined || Number.isNaN(this)) return null;
  if (n <= 0) return Math.round(this);
  const base = Math.pow(10, n);
  return Math.round(this * base) / base;
}
*/

export const numberFormat = (v: number | null | undefined, nullText = '') => {
  if (v === null || v === undefined || Number.isNaN(v)) return nullText;
  return v.toLocaleString(undefined, { maximumFractionDigits: 20 });
};
/*
if (Number.prototype.numberFormat === undefined) Number.prototype.numberFormat = function (this: number, nullText: string = ''): string {
  if (this === null || this === undefined || Number.isNaN(this)) return nullText;
  return this.toLocaleString(undefined, { maximumFractionDigits: 20 });
};
*/

export const safeParseInt = (value: string | number | null | undefined, defaultValue?: number): number | undefined => {
  if (Array.isArray(value) || value === null || value === undefined) return defaultValue;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    try {
      return parseInt(value);
    } catch {
      return defaultValue;
    }
  }
  return defaultValue;
}

export const safeParseFloat = (value: string | number | null | undefined, defaultValue?: number): number | undefined => {
  if (Array.isArray(value) || value === null || value === undefined) return defaultValue;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    try {
      return parseFloat(value);
    } catch {
      return defaultValue;
    }
  }
  return defaultValue;
}

const WarekiGengo = {
  '令和': 2019,
  '平成': 1989,
  '昭和': 1926,
  '大正': 1912,
  '明治': 1868
} as const
type Gengo = keyof typeof WarekiGengo

export const safeParseDate = (dateText: Date | string | null | undefined): Date | undefined => {
  if (typeof dateText !== 'string' || dateText === null || dateText === undefined) return undefined
  try {
    if (dateText.endsWith("Z")) {
      const utcDate = new Date(dateText)
      const date = new Date(utcDate.toLocaleString('ja-JP', { timeZone: "UTC" }))
      return isValidDate(date) ? date : undefined
    }
    const warekiMatch = dateText.match(new RegExp(`(${Object.keys(WarekiGengo).join('|')})(\\d+|元)年(\\d+)月(\\d+)日`))
    if (warekiMatch) {
      const year = WarekiGengo[warekiMatch[1] as Gengo] + (warekiMatch[2] === '元' ? 1 : parseInt(warekiMatch[2], 10)) - 1
      const date = new Date(year, parseInt(warekiMatch[3], 10) - 1, parseInt(warekiMatch[4], 10));
      return isValidDate(date) ? date : undefined
    }
    const seirekiMatch = dateText.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
    if (seirekiMatch) {
      const date = new Date(parseInt(seirekiMatch[1], 10), parseInt(seirekiMatch[2], 10) - 1, parseInt(seirekiMatch[3], 10));
      return isValidDate(date) ? date : undefined
    }
    const date = new Date(dateText)
    return isValidDate(date) ? date : undefined
  } catch {
    return undefined
  }
}

export const convertToFormDateTime = (iso: string | null | undefined): string | null => {
	if (!iso || iso === '') return null
	const str = formatDateTime(safeParseDate(iso) ?? null, '/', ' ', ':')
	if (!str || str === '') return null
	return str
}

export const convertToISODateTime = (formValue: string | null | undefined): string | null => {
	if (!formValue || formValue === '') return null
	const str = formatDateTimeISO(safeParseDate(formValue) ?? null)
	if (str === '') return null
	return str.substring(0, 'yyyy/mm/dd hh:mm'.length)
}

// 最も大きいtextを返す
export const maxText = (...texts: (string | null | undefined)[]): string | null => {
  return texts.reduce((acc, text) => acc ? text ? text > acc ? text : acc : acc : text, null) ?? null
}

export const ONEDAY_MS = 24 * 60 * 60 * 1000
export const LOCALTIME_OFFSET = 9 * 60 * 60 * 1000 // +09:00(Tokyo/Japan)

export const DateToDay = (date: Date, offset: number = LOCALTIME_OFFSET) => Math.floor((date.getTime() + offset) / ONEDAY_MS)
export const DayToDate = (day: number, offset: number = LOCALTIME_OFFSET) => new Date((day * ONEDAY_MS) - offset)
export const TruncDate = (date: Date, offset: number = LOCALTIME_OFFSET) => DayToDate(DateToDay(date, offset), offset)

declare global {
  interface Array<T> {
    getOne: () => T | null,
    distinct: () => Array<T>
  }
}
export const getOne = <T>(array: Array<T> | null | undefined) => {
  if (!array || array.length !== 1) return null;
  return array[0];
}
/*
if (Array.prototype.getOne === undefined) Array.prototype.getOne = function <T>(this: Array<T>): T | null {
  return getOne(this);
}
*/

export const distinct = <T>(array: Array<T>) => {
  if (!array || array.length === 0) return [];
  return Array.from(new Set(array));
}
/*
if (Array.prototype.distinct === undefined) Array.prototype.distinct = function <T>(this: Array<T>): Array<T> {
  return distinct(this);
}
*/

export const reduce = <T, R>(array: Array<T> | undefined, callBack: (value: R, record: T) => R, defaultValue: R) => {
  if (!array || array.length === 0) return defaultValue
  return array.reduce(callBack, defaultValue)
}

export const inList = (wordList: string[], name: string) => wordList.some((word) => name === word);

export const dateFormat = (date?: string | Date | null, option?: {
  nullText?: string;
  prefix?: string;
  suffix?: string;
  wareki?: boolean,
  month?: boolean,
  day?: boolean,
}): string => {
  if (date === null || date === undefined) return option?.nullText ?? '';
  if (typeof date === 'string') {
    try {
      return dateFormat(safeParseDate(date), option);
    } catch {
      return option?.nullText ?? '';
    }
  }
  if (isNaN(date.getTime())) return option?.nullText ?? '';
  const year = date.toLocaleDateString(undefined, { year: 'numeric' });
  const wareki = option?.wareki === false ? '' : `(${date.toLocaleDateString('ja-JP-u-ca-japanese', { year: 'numeric' })})`;
  const monDate = (option?.month === false && option?.day === false) ? '' : (
    date.toLocaleDateString(undefined, {
      month: option?.month === false ? undefined : 'long',
      day: option?.day === false ? undefined : 'numeric'
    })
  );
  return `${option?.prefix ?? ''}${year}${wareki}${monDate}${option?.suffix ?? ''}`;
}
export const dateStampFormat = (
  date?: string | Date | null,
  nullText = ''
): string => {
  if (date === null || date === undefined) return nullText;
  if (typeof date === 'string') {
    try {
      return dateStampFormat(safeParseDate(date));
    } catch {
      return nullText;
    }
  }
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short'
  });
}

export const dateTimeStampFormat = (
  date?: string | Date | null,
  options?: {
    nullText?: string
    separator?: string
  }
): string => {
  if (date === null || date === undefined) return options?.nullText ?? ''
  if (typeof date === 'string') {
    try {
      return dateTimeStampFormat(safeParseDate(date), options)
    } catch {
      return options?.nullText ?? ''
    }
  }
  return `${dateStampFormat(date)}${options?.separator ?? ' '}${timeStampFormat(date)}`
}

export const timeStampFormat = (
  time?: string | Date | null,
  options?: {
    nullText?: string
  }
): string => {
  if (time === null || time === undefined) return options?.nullText ?? ''
  if (typeof time === 'string') {
    try {
      return timeStampFormat(safeParseDate(time), options)
    } catch {
      return options?.nullText ?? ''
    }
  }
  return `${zeroPadding(time.getHours(), 2)}:${zeroPadding(time.getMinutes(), 2)}`
}

// 無効な日付？
export const isValidDate = (date: Date) => date instanceof Date && !Number.isNaN(date.getTime())

// 日付を文字列化
export const formatDate = (date: Date | null, separator = '/') => {
  if (!date || !isValidDate(date)) return null
  return [
    `${zeroPadding(date.getFullYear())}`,
    `${zeroPadding(date.getMonth() + 1, 2)}`,
    `${zeroPadding(date.getDate(), 2)}`
  ].join(separator)
}
export const formatDateTime = (dateTime: Date | null, dateSeparator = '-', separator = 'T', timeSeparator = ':') => {
  if (!dateTime || !isValidDate(dateTime)) return null
  return [
    `${zeroPadding(dateTime.getFullYear())}`,
    `${zeroPadding(dateTime.getMonth() + 1, 2)}`,
    `${zeroPadding(dateTime.getDate(), 2)}`
  ].join(dateSeparator) + separator + [
    `${zeroPadding(dateTime.getHours(), 2)}`,
    `${zeroPadding(dateTime.getMinutes(), 2)}`,
    `${zeroPadding(dateTime.getSeconds(), 2)}`
  ].join(timeSeparator)
}
export const formatDateTimeISO = (dateTime: Date | null) => formatDateTime(dateTime, '-', 'T', ':') || ''
export const formatDateISO = (date: Date | null) => formatDate(date, '-') || ''

export const flatten = <T>(list: (T | undefined | null)[]): T[] => {
  return list.flatMap((v) => {
    if (v === undefined || v === null) return [];
    if (Array.isArray(v)) {
      return v.map((sub) => {
        if (typeof sub === 'number') return `${sub}`;
        return sub;
      })
    }
    return v;
  });
}
export const flatJoin = (separator: string, ...paths: (number | number[] | string | string[] | undefined | null)[]) => {
  return paths.flatMap((path) => {
    if (path === undefined || path === null || path === '') return [];
    if (Array.isArray(path)) {
      return path.map((sub) => {
        if (typeof sub === 'number') return `${sub}`;
        return sub;
      })
    }
    if (typeof path === 'number') return `${path}`;
    return path;
  }).join(separator);
}

export const splitTextInRect = (text: string, fontPoint: number, fontFace: string, width: number) => {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;

  ctx.font = `${fontPoint}pt "${fontFace}"`;

  const words: {
    text: string;
    suffix?: string;
  }[] = [];

  let start = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const after = text.substring(start);
    // eslint-disable-next-line no-irregular-whitespace
    const at = after.search(/[ |　|-|‑]/g);
    if (at < 0) {
      words.push({ text: after })
      break;
    }
    const word = after.substring(0, at);
    words.push({
      text: word,
      suffix: after.substring(at, at + 1)
    });
    start = start + at + 1;
  }

  const lines: string[] = [];
  const addLine = (add: string) => {
    if (add === '') return '';
    if (ctx.measureText(add).width < width) {
      lines.push(add);
      return '';
    }
    for(let i = 1; i <= add.length; i++) {
      const t = add.substring(0, i);
      const w = ctx.measureText(t).width;
      if (w >= width) {
        lines.push(add.substring(0, i-1));
        return add.substring(i-1);
      }
    }
    lines.push(add);
    return '';
  }

  let line = '';
  let prefix = '';
  let wordCount = 0;
  for(const word of words) {
    const target = line.trim() + prefix + word.text;
    if (ctx.measureText(target).width >= width) {
      if (wordCount > 0 && /^[\x20-\x7e]+$/.test(target)) {
        // wrap by word
        lines.push(line);
        line = prefix + word.text;
      } else {
        // wrap by charctor
        line = addLine(target);
      }
      wordCount = 0;
    } else {
      line = target;
      wordCount++;
    }
    prefix = word.suffix ?? '';
  }
  while (line !== '') {
    line = addLine(line.trim());
  }  
  return lines;
}

// "Xxxxx"の形に整形
export const camelCase = (name: string) => name.length === 0 ? '' : name.substring(0, 1).toUpperCase() + name.substring(1).toLowerCase();
// ゼロ埋め
export const zeroPadding = (n: number, l = 0) => (Array(l).join('0') + n).slice(-l);

export const setDocumentTitle = (...paths: (string | undefined)[]) => {
  document.title = flatJoin(' | ', ...paths, `${CareNet} ${SystemName}`)
}

export const addDateTime = (dt: Date | undefined | null, interval: number, unit: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second') => {
  if (!dt) return null
  const ret = new Date(dt)
  switch (unit) {
    case 'year':
      ret.setFullYear(ret.getFullYear() + interval)
      break
    case 'month':
      ret.setMonth(ret.getMonth() + interval)
      break
    case 'day':
      ret.setDate(ret.getDate() + interval)
      break
    case 'hour':
      ret.setHours(ret.getHours() + interval)
      break
    case 'minute':
      ret.setMinutes(ret.getMinutes() + interval)
      break
    case 'second':
      ret.setSeconds(ret.getSeconds() + interval)
      break
  }
  return ret
}